import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Message } from 'primereact/message';
import { authApi } from '../api/auth';

export const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!password) {
      setError('Please enter a password');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!token) {
      setError('Invalid or missing reset token');
      return;
    }

    setIsSubmitting(true);

    try {
      await authApi.resetPassword(token, password);
      setIsSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err: unknown) {
      let errorMessage = 'Failed to reset password';
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { detail?: string } } };
        if (axiosError.response?.data?.detail) {
          errorMessage = axiosError.response.data.detail;
        }
      }
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="auth-layout">
        <Card className="auth-card">
          <div className="auth-header">
            <img src="/tallai-icon.png" alt="TallAI" />
            <h1>tall<b>ai</b></h1>
          </div>
          <Message severity="error" text="Invalid or missing reset token" className="form-error" />
          <div className="auth-footer">
            <p><Link to="/forgot-password">Request a new reset link</Link></p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="auth-layout">
      <Card className="auth-card">
        <div className="auth-header">
          <img src="/tallai-icon.png" alt="TallAI" />
          <h1>tall<b>ai</b></h1>
        </div>

        {isSuccess ? (
          <div className="auth-success">
            <Message severity="success" text="Password reset successfully! Redirecting to login..." />
            <Link to="/login" className="auth-link">Go to Sign In</Link>
          </div>
        ) : (
          <>
            <p className="auth-subtitle">Enter your new password below.</p>

            {error && (
              <Message severity="error" text={error} className="form-error" />
            )}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="field">
                <label htmlFor="password">New Password</label>
                <Password
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  toggleMask
                  feedback={false}
                  disabled={isSubmitting}
                />
              </div>

              <div className="field">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <Password
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  toggleMask
                  feedback={false}
                  disabled={isSubmitting}
                />
              </div>

              <Button
                type="submit"
                label={isSubmitting ? 'Resetting...' : 'Reset Password'}
                icon="ms-out ms-out-lock-reset"
                className="submit-button"
                disabled={isSubmitting}
                loading={isSubmitting}
              />
            </form>

            <Link to="/login" className="auth-link">Back to Sign In</Link>
          </>
        )}
      </Card>
    </div>
  );
};

export default ResetPassword;
